/**
 * Reusable modal forms for HR module.
 * Uses shadcn Dialog. Compact single-file to reduce boilerplate.
 */
import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "./ui/dialog";
import {
    hrCreateClient, hrUpdateClient, hrCreateEmployee, hrUpdateEmployee,
    hrCreateDispute, hrUpdateDispute, hrCreateDisciplinary, hrUpdateDisciplinary,
    hrCcnlPresets, formatApiError,
} from "../lib/api";
import { Loader2, AlertCircle } from "lucide-react";

const inputBase = "w-full px-3 py-2 bg-white border border-slate-300 text-sm focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold";
const labelBase = "block text-[11px] uppercase tracking-[0.18em] font-bold text-slate-600 mb-1.5";
const btnPrimary = "px-5 py-2.5 bg-navy text-white text-xs uppercase tracking-[0.2em] font-bold hover:bg-navy/90 disabled:opacity-50 inline-flex items-center gap-2";
const btnSecondary = "px-5 py-2.5 border border-slate-300 text-slate-700 text-xs uppercase tracking-[0.2em] font-bold hover:border-slate-500";

function ErrorBox({ text }) {
    if (!text) return null;
    return (
        <div className="p-3 border border-red-300 bg-red-50 text-red-700 text-sm flex items-start gap-2" data-testid="form-error">
            <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {text}
        </div>
    );
}

// ============ CLIENT FORM ============
export function HRClientDialog({ open, onOpenChange, client, onSaved }) {
    const isEdit = !!client;
    const [form, setForm] = useState({
        ragione_sociale: "", partita_iva: "", settore: "", ccnl_preset: "custom",
        ccnl_nome: "", limite_td_pct: "", limite_somm_pct: "", limite_totale_pct: "",
        referente: "", email: "", telefono: "", note: "",
        dvr_data_revisione: "", rspp_nomina_data: "", rls_nomina_data: "",
        addetti_antincendio_scadenza: "", addetti_primo_soccorso_scadenza: "",
        dvr_assente: false, rspp_non_nominato: false, rls_non_nominato: false,
        antincendio_non_nominati: false, primo_soccorso_non_nominati: false,
    });
    const [presets, setPresets] = useState({});
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        hrCcnlPresets().then((d) => setPresets(d.presets)).catch(() => {});
        if (client) {
            setForm({
                ragione_sociale: client.ragione_sociale || "",
                partita_iva: client.partita_iva || "",
                settore: client.settore || "",
                ccnl_preset: client.ccnl_preset || "custom",
                ccnl_nome: client.ccnl_nome || "",
                limite_td_pct: client.limite_td_pct ?? "",
                limite_somm_pct: client.limite_somm_pct ?? "",
                limite_totale_pct: client.limite_totale_pct ?? "",
                referente: client.referente || "",
                email: client.email || "",
                telefono: client.telefono || "",
                note: client.note || "",
                dvr_data_revisione: client.dvr_data_revisione || "",
                rspp_nomina_data: client.rspp_nomina_data || "",
                rls_nomina_data: client.rls_nomina_data || "",
                addetti_antincendio_scadenza: client.addetti_antincendio_scadenza || "",
                addetti_primo_soccorso_scadenza: client.addetti_primo_soccorso_scadenza || "",
                dvr_assente: !!client.dvr_assente,
                rspp_non_nominato: !!client.rspp_non_nominato,
                rls_non_nominato: !!client.rls_non_nominato,
                antincendio_non_nominati: !!client.antincendio_non_nominati,
                primo_soccorso_non_nominati: !!client.primo_soccorso_non_nominati,
            });
        } else {
            setForm({
                ragione_sociale: "", partita_iva: "", settore: "", ccnl_preset: "custom",
                ccnl_nome: "", limite_td_pct: "", limite_somm_pct: "", limite_totale_pct: "",
                referente: "", email: "", telefono: "", note: "",
                dvr_data_revisione: "", rspp_nomina_data: "", rls_nomina_data: "",
                addetti_antincendio_scadenza: "", addetti_primo_soccorso_scadenza: "",
                dvr_assente: false, rspp_non_nominato: false, rls_non_nominato: false,
                antincendio_non_nominati: false, primo_soccorso_non_nominati: false,
            });
        }
        setError(null);
    }, [open, client]);

    const handlePresetChange = (key) => {
        const p = presets[key];
        setForm((f) => ({
            ...f,
            ccnl_preset: key,
            ccnl_nome: p ? p.nome : f.ccnl_nome,
            limite_td_pct: p ? p.limite_tempo_determinato_pct : "",
            limite_somm_pct: p ? p.limite_somministrazione_pct : "",
            limite_totale_pct: p ? p.limite_totale_pct : "",
        }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        const payload = { ...form };
        // Convert empty strings to null for numeric fields
        ["limite_td_pct", "limite_somm_pct", "limite_totale_pct"].forEach((k) => {
            payload[k] = payload[k] === "" ? null : Number(payload[k]);
        });
        // Convert empty date strings to null
        ["dvr_data_revisione", "rspp_nomina_data", "rls_nomina_data",
         "addetti_antincendio_scadenza", "addetti_primo_soccorso_scadenza"].forEach((k) => {
            if (payload[k] === "") payload[k] = null;
        });
        try {
            if (isEdit) await hrUpdateClient(client.id, payload);
            else await hrCreateClient(payload);
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-client-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-navy text-2xl">
                        {isEdit ? "Modifica Cliente" : "Nuovo Cliente"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        {isEdit ? "Aggiorna i dati anagrafici e CCNL del cliente." : "Registra una nuova azienda cliente per iniziare il monitoraggio Legal HR."}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className={labelBase}>Ragione Sociale *</label>
                        <input required data-testid="input-ragione-sociale" className={inputBase}
                            value={form.ragione_sociale} onChange={(e) => setForm({ ...form, ragione_sociale: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelBase}>Partita IVA</label>
                            <input className={inputBase} value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Settore</label>
                            <input className={inputBase} value={form.settore} onChange={(e) => setForm({ ...form, settore: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>CCNL Applicato *</label>
                        <select data-testid="select-ccnl-preset" className={inputBase} value={form.ccnl_preset}
                            onChange={(e) => handlePresetChange(e.target.value)}>
                            {Object.entries(presets).map(([k, p]) => (
                                <option key={k} value={k}>{p.nome}</option>
                            ))}
                        </select>
                        {presets[form.ccnl_preset]?.note && (
                            <div className="text-xs text-slate-500 mt-1.5 italic">{presets[form.ccnl_preset].note}</div>
                        )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={labelBase}>Limite TD %</label>
                            <input type="number" step="0.1" min="0" max="100" className={inputBase}
                                value={form.limite_td_pct} onChange={(e) => setForm({ ...form, limite_td_pct: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Limite Somm. %</label>
                            <input type="number" step="0.1" min="0" max="100" className={inputBase}
                                value={form.limite_somm_pct} onChange={(e) => setForm({ ...form, limite_somm_pct: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Limite Totale %</label>
                            <input type="number" step="0.1" min="0" max="100" className={inputBase}
                                value={form.limite_totale_pct} onChange={(e) => setForm({ ...form, limite_totale_pct: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={labelBase}>Referente</label>
                            <input className={inputBase} value={form.referente} onChange={(e) => setForm({ ...form, referente: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Email</label>
                            <input type="email" className={inputBase} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Telefono</label>
                            <input className={inputBase} value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} />
                        </div>
                    </div>
                    <div className="pt-2 border-t border-slate-200">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-slate-700 mb-1 mt-3">Sicurezza aziendale — D.Lgs. 81/2008</div>
                        <div className="text-xs text-slate-500 italic mb-3">Date delle ultime nomine/revisioni. Spunta la casella se la figura/documento è assente: il sistema genererà un alert con la sanzione applicabile.</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[
                                {dateKey: "dvr_data_revisione", flagKey: "dvr_assente", label: "DVR — Ultima revisione", helper: "Revisione annuale (art. 29 D.Lgs. 81/2008)", flagLabel: "DVR non redatto/aggiornato"},
                                {dateKey: "rspp_nomina_data", flagKey: "rspp_non_nominato", label: "RSPP — Data nomina", helper: "Aggiornamento quinquennale (art. 32)", flagLabel: "RSPP non nominato"},
                                {dateKey: "rls_nomina_data", flagKey: "rls_non_nominato", label: "RLS — Data elezione/nomina", helper: "Formazione triennale (art. 47)", flagLabel: "RLS non eletto/nominato"},
                                {dateKey: "addetti_antincendio_scadenza", flagKey: "antincendio_non_nominati", label: "Addetti antincendio — Scadenza aggiornamento", helper: "DM 02/09/2021 — aggiornamento 3/5 anni", flagLabel: "Addetti antincendio non designati"},
                                {dateKey: "addetti_primo_soccorso_scadenza", flagKey: "primo_soccorso_non_nominati", label: "Addetti primo soccorso — Scadenza aggiornamento", helper: "DM 388/2003 — aggiornamento triennale", flagLabel: "Addetti primo soccorso non designati"},
                            ].map((f) => (
                                <div key={f.dateKey}>
                                    <label className={labelBase}>{f.label}</label>
                                    <input type="date" data-testid={`input-${f.dateKey}`} className={inputBase} disabled={!!form[f.flagKey]}
                                        value={form[f.dateKey]} onChange={(e) => setForm({ ...form, [f.dateKey]: e.target.value })} />
                                    <label className="mt-2 flex items-center gap-2 text-xs text-red-700 font-medium cursor-pointer">
                                        <input type="checkbox" data-testid={`flag-${f.flagKey}`} checked={!!form[f.flagKey]}
                                            onChange={(e) => setForm({ ...form, [f.flagKey]: e.target.checked, ...(e.target.checked ? {[f.dateKey]: ""} : {}) })} />
                                        {f.flagLabel}
                                    </label>
                                    <div className="text-[10px] text-slate-500 mt-1">{f.helper}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>Note</label>
                        <textarea rows="3" className={inputBase} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </div>
                    <ErrorBox text={error} />
                    <DialogFooter className="gap-2">
                        <button type="button" className={btnSecondary} onClick={() => onOpenChange(false)}>Annulla</button>
                        <button type="submit" disabled={saving} className={btnPrimary} data-testid="btn-save-client">
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {isEdit ? "Salva Modifiche" : "Crea Cliente"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ============ EMPLOYEE FORM ============
export function HREmployeeDialog({ open, onOpenChange, clientId, employee, onSaved }) {
    const isEdit = !!employee;
    const [form, setForm] = useState({
        nome_completo: "", codice_fiscale: "", mansione: "", livello_ccnl: "",
        tipologia_contratto: "tempo_indeterminato", data_inizio: "", data_fine: "",
        numero_proroghe: 0, presenza_causale: false, causale_testo: "",
        visita_medica_scadenza: "", visita_non_svolta: false,
        formazione_sicurezza_scadenza: "", formazione_non_svolta: false, note: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        if (employee) {
            setForm({
                nome_completo: employee.nome_completo || "",
                codice_fiscale: employee.codice_fiscale || "",
                mansione: employee.mansione || "",
                livello_ccnl: employee.livello_ccnl || "",
                tipologia_contratto: employee.tipologia_contratto || "tempo_indeterminato",
                data_inizio: employee.data_inizio || "",
                data_fine: employee.data_fine || "",
                numero_proroghe: employee.numero_proroghe ?? 0,
                presenza_causale: !!employee.presenza_causale,
                causale_testo: employee.causale_testo || "",
                visita_medica_scadenza: employee.visita_medica_scadenza || "",
                visita_non_svolta: !!employee.visita_non_svolta,
                formazione_sicurezza_scadenza: employee.formazione_sicurezza_scadenza || "",
                formazione_non_svolta: !!employee.formazione_non_svolta,
                note: employee.note || "",
            });
        } else {
            setForm({
                nome_completo: "", codice_fiscale: "", mansione: "", livello_ccnl: "",
                tipologia_contratto: "tempo_indeterminato", data_inizio: "", data_fine: "",
                numero_proroghe: 0, presenza_causale: false, causale_testo: "",
                visita_medica_scadenza: "", visita_non_svolta: false,
                formazione_sicurezza_scadenza: "", formazione_non_svolta: false, note: "",
            });
        }
        setError(null);
    }, [open, employee]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        const payload = { ...form };
        // Convert empty strings to null for optional dates
        ["data_fine", "visita_medica_scadenza", "formazione_sicurezza_scadenza"].forEach((k) => {
            if (payload[k] === "") payload[k] = null;
        });
        payload.numero_proroghe = Number(payload.numero_proroghe) || 0;
        try {
            if (isEdit) {
                await hrUpdateEmployee(employee.id, payload);
            } else {
                await hrCreateEmployee({ ...payload, client_id: clientId });
            }
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const isTD = form.tipologia_contratto === "tempo_determinato";

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-employee-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-navy text-2xl">
                        {isEdit ? "Modifica Dipendente" : "Nuovo Dipendente"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        Dati anagrafici, contrattuali e scadenze D.Lgs. 81/08.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div>
                        <label className={labelBase}>Nome Completo *</label>
                        <input required data-testid="input-nome-dipendente" className={inputBase}
                            value={form.nome_completo} onChange={(e) => setForm({ ...form, nome_completo: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelBase}>Codice Fiscale</label>
                            <input className={inputBase} value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} />
                        </div>
                        <div>
                            <label className={labelBase}>Livello CCNL</label>
                            <input className={inputBase} placeholder="Es. 3° livello" value={form.livello_ccnl} onChange={(e) => setForm({ ...form, livello_ccnl: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>Mansione</label>
                        <input className={inputBase} value={form.mansione} onChange={(e) => setForm({ ...form, mansione: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={labelBase}>Tipologia Contratto *</label>
                            <select required data-testid="select-tipologia" className={inputBase}
                                value={form.tipologia_contratto} onChange={(e) => setForm({ ...form, tipologia_contratto: e.target.value })}>
                                <option value="tempo_indeterminato">Tempo Indeterminato</option>
                                <option value="tempo_determinato">Tempo Determinato</option>
                                <option value="somministrazione">Somministrazione</option>
                                <option value="apprendistato">Apprendistato</option>
                                <option value="collaborazione">Collaborazione</option>
                                <option value="stage">Stage / Tirocinio</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelBase}>Data Inizio *</label>
                            <input required type="date" data-testid="input-data-inizio" className={inputBase}
                                value={form.data_inizio} onChange={(e) => setForm({ ...form, data_inizio: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Data Fine</label>
                            <input type="date" className={inputBase}
                                value={form.data_fine || ""} onChange={(e) => setForm({ ...form, data_fine: e.target.value })} />
                        </div>
                    </div>
                    {isTD && (
                        <div className="p-4 bg-amber-50 border border-amber-200 space-y-3">
                            <div className="text-xs uppercase tracking-[0.18em] font-bold text-amber-800">Sezione Contratto a Termine</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelBase}>N. Proroghe / Rinnovi</label>
                                    <input type="number" min="0" max="20" className={inputBase}
                                        value={form.numero_proroghe} onChange={(e) => setForm({ ...form, numero_proroghe: e.target.value })} />
                                </div>
                                <div className="flex items-end">
                                    <label className="flex items-center gap-2 pb-2 cursor-pointer">
                                        <input type="checkbox" data-testid="check-causale"
                                            checked={form.presenza_causale} onChange={(e) => setForm({ ...form, presenza_causale: e.target.checked })} />
                                        <span className="text-sm text-slate-700">Causale valida presente (art. 19 D.lgs. 81/2015)</span>
                                    </label>
                                </div>
                            </div>
                            {form.presenza_causale && (
                                <div>
                                    <label className={labelBase}>Testo Causale</label>
                                    <input className={inputBase} value={form.causale_testo}
                                        onChange={(e) => setForm({ ...form, causale_testo: e.target.value })} />
                                </div>
                            )}
                        </div>
                    )}
                    <div className="p-4 bg-slate-50 border border-slate-200 space-y-4">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-600">Adempimenti D.Lgs. 81/2008</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelBase}>Scadenza Visita Medica</label>
                                <input type="date" className={inputBase} disabled={form.visita_non_svolta}
                                    value={form.visita_medica_scadenza || ""} onChange={(e) => setForm({ ...form, visita_medica_scadenza: e.target.value })} />
                                <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm">
                                    <input type="checkbox" data-testid="check-visita-non-svolta"
                                        checked={form.visita_non_svolta}
                                        onChange={(e) => setForm({ ...form, visita_non_svolta: e.target.checked, ...(e.target.checked ? { visita_medica_scadenza: "" } : {}) })} />
                                    <span className={form.visita_non_svolta ? "text-red-700 font-bold" : "text-slate-600"}>Visita NON svolta</span>
                                </label>
                            </div>
                            <div>
                                <label className={labelBase}>Scadenza Formazione Sicurezza</label>
                                <input type="date" className={inputBase} disabled={form.formazione_non_svolta}
                                    value={form.formazione_sicurezza_scadenza || ""} onChange={(e) => setForm({ ...form, formazione_sicurezza_scadenza: e.target.value })} />
                                <label className="flex items-center gap-2 mt-2 cursor-pointer text-sm">
                                    <input type="checkbox" data-testid="check-formazione-non-svolta"
                                        checked={form.formazione_non_svolta}
                                        onChange={(e) => setForm({ ...form, formazione_non_svolta: e.target.checked, ...(e.target.checked ? { formazione_sicurezza_scadenza: "" } : {}) })} />
                                    <span className={form.formazione_non_svolta ? "text-red-700 font-bold" : "text-slate-600"}>Formazione NON svolta</span>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>Note</label>
                        <textarea rows="2" className={inputBase} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </div>
                    <ErrorBox text={error} />
                    <DialogFooter className="gap-2">
                        <button type="button" className={btnSecondary} onClick={() => onOpenChange(false)}>Annulla</button>
                        <button type="submit" disabled={saving} className={btnPrimary} data-testid="btn-save-employee">
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {isEdit ? "Salva Modifiche" : "Crea Dipendente"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ============ DISPUTE FORM ============
export function HRDisputeDialog({ open, onOpenChange, clientId, dispute, onSaved }) {
    const isEdit = !!dispute;
    const [form, setForm] = useState({
        lavoratore: "", oggetto: "", tipo: "", petitum: 0, spese_legali_stimate: 0,
        valutazione_rischio: "possibile", avvocato_domiciliatario: "",
        tribunale: "", numero_ruolo: "", stato_giudizio: "prima_udienza",
        data_apertura: "", prossima_udienza: "", note: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        if (dispute) {
            setForm({
                lavoratore: dispute.lavoratore || "",
                oggetto: dispute.oggetto || "",
                tipo: dispute.tipo || "",
                petitum: dispute.petitum ?? 0,
                spese_legali_stimate: dispute.spese_legali_stimate ?? 0,
                valutazione_rischio: dispute.valutazione_rischio || "possibile",
                avvocato_domiciliatario: dispute.avvocato_domiciliatario || "",
                tribunale: dispute.tribunale || "",
                numero_ruolo: dispute.numero_ruolo || "",
                stato_giudizio: dispute.stato_giudizio || "prima_udienza",
                data_apertura: dispute.data_apertura || "",
                prossima_udienza: dispute.prossima_udienza || "",
                note: dispute.note || "",
            });
        } else {
            setForm({
                lavoratore: "", oggetto: "", tipo: "", petitum: 0, spese_legali_stimate: 0,
                valutazione_rischio: "possibile", avvocato_domiciliatario: "",
                tribunale: "", numero_ruolo: "", stato_giudizio: "prima_udienza",
                data_apertura: "", prossima_udienza: "", note: "",
            });
        }
        setError(null);
    }, [open, dispute]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        const payload = { ...form };
        ["data_apertura", "prossima_udienza"].forEach((k) => { if (payload[k] === "") payload[k] = null; });
        payload.petitum = Number(payload.petitum) || 0;
        payload.spese_legali_stimate = Number(payload.spese_legali_stimate) || 0;
        try {
            if (isEdit) await hrUpdateDispute(dispute.id, payload);
            else await hrCreateDispute({ ...payload, client_id: clientId });
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-dispute-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-navy text-2xl">
                        {isEdit ? "Modifica Contenzioso" : "Nuovo Contenzioso"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        Registra una causa attiva e il rischio economico stimato per il fondo rischi.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelBase}>Lavoratore *</label>
                            <input required data-testid="input-lavoratore" className={inputBase}
                                value={form.lavoratore} onChange={(e) => setForm({ ...form, lavoratore: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Tipo Causa</label>
                            <select className={inputBase} value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}>
                                <option value="">— Seleziona —</option>
                                <option value="Licenziamento">Licenziamento</option>
                                <option value="Inquadramento">Inquadramento</option>
                                <option value="Differenze retributive">Differenze retributive</option>
                                <option value="Mobbing">Mobbing / Danno alla persona</option>
                                <option value="Demansionamento">Demansionamento</option>
                                <option value="Contratto TD/Somministrazione">Contratto TD/Somministrazione</option>
                                <option value="Trasferimento">Trasferimento</option>
                                <option value="Sicurezza sul lavoro">Sicurezza sul lavoro</option>
                                <option value="Altro">Altro</option>
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>Oggetto Sintetico *</label>
                        <input required data-testid="input-oggetto" className={inputBase}
                            placeholder="Es. Impugnazione licenziamento per giustificato motivo oggettivo"
                            value={form.oggetto} onChange={(e) => setForm({ ...form, oggetto: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className={labelBase}>Petitum (€) *</label>
                            <input required type="number" min="0" step="100" data-testid="input-petitum" className={inputBase}
                                value={form.petitum} onChange={(e) => setForm({ ...form, petitum: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Spese Legali Stimate (€)</label>
                            <input type="number" min="0" step="100" className={inputBase}
                                value={form.spese_legali_stimate} onChange={(e) => setForm({ ...form, spese_legali_stimate: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Valutazione Rischio *</label>
                            <select required data-testid="select-rischio" className={inputBase}
                                value={form.valutazione_rischio} onChange={(e) => setForm({ ...form, valutazione_rischio: e.target.value })}>
                                <option value="probabile">Probabile (80-100%)</option>
                                <option value="possibile">Possibile (30-50%)</option>
                                <option value="remoto">Remoto</option>
                            </select>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelBase}>Avvocato Domiciliatario</label>
                            <input className={inputBase} value={form.avvocato_domiciliatario} onChange={(e) => setForm({ ...form, avvocato_domiciliatario: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Tribunale</label>
                            <input className={inputBase} value={form.tribunale} onChange={(e) => setForm({ ...form, tribunale: e.target.value })} />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className={labelBase}>N. R.G.</label>
                            <input className={inputBase} value={form.numero_ruolo} onChange={(e) => setForm({ ...form, numero_ruolo: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Stato Giudizio</label>
                            <select className={inputBase} value={form.stato_giudizio} onChange={(e) => setForm({ ...form, stato_giudizio: e.target.value })}>
                                <option value="prima_udienza">Prima udienza</option>
                                <option value="istruttoria">Istruttoria</option>
                                <option value="sentenza_1grado">Sentenza 1° grado</option>
                                <option value="appello">Appello</option>
                                <option value="cassazione">Cassazione</option>
                                <option value="conciliato">Conciliato</option>
                                <option value="definito">Definito</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelBase}>Data Apertura</label>
                            <input type="date" className={inputBase} value={form.data_apertura || ""} onChange={(e) => setForm({ ...form, data_apertura: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Prossima Udienza</label>
                            <input type="date" className={inputBase} value={form.prossima_udienza || ""} onChange={(e) => setForm({ ...form, prossima_udienza: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>Note</label>
                        <textarea rows="3" className={inputBase} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </div>
                    <ErrorBox text={error} />
                    <DialogFooter className="gap-2">
                        <button type="button" className={btnSecondary} onClick={() => onOpenChange(false)}>Annulla</button>
                        <button type="submit" disabled={saving} className={btnPrimary} data-testid="btn-save-dispute">
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {isEdit ? "Salva Modifiche" : "Crea Contenzioso"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

// ============ DISCIPLINARY FORM ============
export function HRDisciplinaryDialog({ open, onOpenChange, clientId, disciplinary, onSaved }) {
    const isEdit = !!disciplinary;
    const [form, setForm] = useState({
        lavoratore: "", contestazione_data: "", fatti_contestati: "",
        sanzione_proposta: "", giustificazioni_ricevute: false, data_giustificazioni: "",
        sanzione_irrogata: "", data_sanzione: "", esito: "in_corso", note: "",
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!open) return;
        if (disciplinary) {
            setForm({
                lavoratore: disciplinary.lavoratore || "",
                contestazione_data: disciplinary.contestazione_data || "",
                fatti_contestati: disciplinary.fatti_contestati || "",
                sanzione_proposta: disciplinary.sanzione_proposta || "",
                giustificazioni_ricevute: !!disciplinary.giustificazioni_ricevute,
                data_giustificazioni: disciplinary.data_giustificazioni || "",
                sanzione_irrogata: disciplinary.sanzione_irrogata || "",
                data_sanzione: disciplinary.data_sanzione || "",
                esito: disciplinary.esito || "in_corso",
                note: disciplinary.note || "",
            });
        } else {
            setForm({
                lavoratore: "", contestazione_data: "", fatti_contestati: "",
                sanzione_proposta: "", giustificazioni_ricevute: false, data_giustificazioni: "",
                sanzione_irrogata: "", data_sanzione: "", esito: "in_corso", note: "",
            });
        }
        setError(null);
    }, [open, disciplinary]);

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);
        const payload = { ...form };
        ["data_giustificazioni", "data_sanzione"].forEach((k) => { if (payload[k] === "") payload[k] = null; });
        try {
            if (isEdit) await hrUpdateDisciplinary(disciplinary.id, payload);
            else await hrCreateDisciplinary({ ...payload, client_id: clientId });
            onSaved?.();
            onOpenChange(false);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hr-disciplinary-dialog">
                <DialogHeader>
                    <DialogTitle className="font-serif text-navy text-2xl">
                        {isEdit ? "Modifica Procedimento Disciplinare" : "Nuovo Procedimento Disciplinare"}
                    </DialogTitle>
                    <DialogDescription className="text-xs text-slate-500">
                        Ricorda: il lavoratore ha 5 giorni lavorativi dalla contestazione per giustificarsi (art. 7 L. 300/1970).
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelBase}>Lavoratore *</label>
                            <input required className={inputBase} value={form.lavoratore} onChange={(e) => setForm({ ...form, lavoratore: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelBase}>Data Contestazione *</label>
                            <input required type="date" className={inputBase}
                                value={form.contestazione_data} onChange={(e) => setForm({ ...form, contestazione_data: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <label className={labelBase}>Fatti Contestati *</label>
                        <textarea required rows="4" className={inputBase} value={form.fatti_contestati}
                            onChange={(e) => setForm({ ...form, fatti_contestati: e.target.value })} />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className={labelBase}>Sanzione Proposta</label>
                            <select className={inputBase} value={form.sanzione_proposta} onChange={(e) => setForm({ ...form, sanzione_proposta: e.target.value })}>
                                <option value="">— Seleziona —</option>
                                <option value="Rimprovero verbale">Rimprovero verbale</option>
                                <option value="Rimprovero scritto">Rimprovero scritto</option>
                                <option value="Multa">Multa</option>
                                <option value="Sospensione">Sospensione</option>
                                <option value="Licenziamento disciplinare">Licenziamento disciplinare</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelBase}>Esito</label>
                            <select className={inputBase} value={form.esito} onChange={(e) => setForm({ ...form, esito: e.target.value })}>
                                <option value="in_corso">In corso</option>
                                <option value="archiviato">Archiviato</option>
                                <option value="sanzionato">Sanzionato</option>
                                <option value="impugnato">Impugnato</option>
                            </select>
                        </div>
                    </div>
                    <div className="p-4 bg-slate-50 border border-slate-200 space-y-3">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input type="checkbox" checked={form.giustificazioni_ricevute}
                                onChange={(e) => setForm({ ...form, giustificazioni_ricevute: e.target.checked })} />
                            <span className="text-sm text-slate-700 font-semibold">Giustificazioni ricevute dal lavoratore</span>
                        </label>
                        {form.giustificazioni_ricevute && (
                            <div>
                                <label className={labelBase}>Data Ricezione Giustificazioni</label>
                                <input type="date" className={inputBase} value={form.data_giustificazioni || ""}
                                    onChange={(e) => setForm({ ...form, data_giustificazioni: e.target.value })} />
                            </div>
                        )}
                    </div>
                    {(form.esito === "sanzionato" || form.esito === "impugnato") && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className={labelBase}>Sanzione Irrogata</label>
                                <input className={inputBase} value={form.sanzione_irrogata}
                                    onChange={(e) => setForm({ ...form, sanzione_irrogata: e.target.value })} />
                            </div>
                            <div>
                                <label className={labelBase}>Data Sanzione</label>
                                <input type="date" className={inputBase} value={form.data_sanzione || ""}
                                    onChange={(e) => setForm({ ...form, data_sanzione: e.target.value })} />
                            </div>
                        </div>
                    )}
                    <div>
                        <label className={labelBase}>Note</label>
                        <textarea rows="2" className={inputBase} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                    </div>
                    <ErrorBox text={error} />
                    <DialogFooter className="gap-2">
                        <button type="button" className={btnSecondary} onClick={() => onOpenChange(false)}>Annulla</button>
                        <button type="submit" disabled={saving} className={btnPrimary} data-testid="btn-save-disciplinary">
                            {saving && <Loader2 size={14} className="animate-spin" />}
                            {isEdit ? "Salva Modifiche" : "Crea Procedimento"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
